// v3.15-1068 (Tier 3) — UI confirmation gate for the three operator-
// authoritative financial fields. Pairs with the backend Tier 2 guard
// in `PUT /api/clients/{id}` (server-side enforcement).
//
// Usage:
//   const { ok, payload } = confirmFinancialChanges(client, body);
//   if (!ok) return;             // operator cancelled — abort the save
//   await api.put(`/clients/${id}`, payload);
//
// If `body` does not change any of the three financial fields, the
// helper returns `{ ok: true, payload: body }` immediately (no prompt).
// If `body` changes one or more fields, the helper shows a `window.confirm`
// with the diff (OLD vs NEW per field). On confirm it injects
// `financial_change_confirm: true` into the payload. On cancel it returns
// `{ ok: false }` so the caller can abort.

const FINANCIAL_FIELDS = ['applied_for_amount', 'deal_amount', 'approved_amount'];

const LABELS = {
  applied_for_amount: 'Applied For Amount',
  deal_amount:        'Deal Amount',
  approved_amount:    'Approved Amount',
};

const fmt = (v) => {
  if (v === null || v === undefined || v === '') return '— (empty)';
  const n = Number(v);
  if (Number.isFinite(n)) return `$${n.toLocaleString()}`;
  return String(v);
};

const normalize = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
};

export const confirmFinancialChanges = (client, body) => {
  if (!body || typeof body !== 'object') return { ok: true, payload: body };

  // v3.15-1152d — Self-confirmed inline edits bypass the prompt. The
  // financing-offer spreadsheet editor (OfferBar) sets this flag when the
  // operator commits a single inline edit via Enter / Tab / blur / cross-
  // cell click. The native `window.confirm` was stealing focus from the
  // active input, blurring it, and closing the editor mid-flow — which
  // looked to operators like "the field randomly exits while I'm typing."
  // The Tier-3 guard's intent (catch accidental bulk programmatic writes)
  // is preserved: any other caller that omits the flag still gets the prompt.
  if (body.financial_change_confirm === true) {
    return { ok: true, payload: body };
  }

  const diffs = [];
  for (const field of FINANCIAL_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(body, field)) {
      const oldV = normalize(client?.[field]);
      const newV = normalize(body[field]);
      if (oldV !== newV) {
        diffs.push({ field, old: oldV, new: newV });
      }
    }
  }

  if (diffs.length === 0) {
    return { ok: true, payload: body };
  }

  const lines = diffs.map(
    (d) => `• ${LABELS[d.field]}:  ${fmt(d.old)}   →   ${fmt(d.new)}`
  );
  const message =
    'You are about to change financial values on this deal:\n\n' +
    lines.join('\n') +
    '\n\nThese are operator-authoritative figures (Applied For / Deal Amount / Approved Amount).\n' +
    'Every change is logged to the audit trail.\n\n' +
    'Confirm to save, or Cancel to keep the existing values.';


  const confirmed = window.confirm(message);
  if (!confirmed) {
    return { ok: false };
  }
  return { ok: true, payload: { ...body, financial_change_confirm: true } };
};

export default confirmFinancialChanges;
